import { createContext, forwardRef, useContext, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

interface RadioGroupCtx {
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

const RadioGroupContext = createContext<RadioGroupCtx | null>(null);

export interface RadioGroupProps {
  name?: string;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  label?: ReactNode;
  description?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function RadioGroup({ name, value, onChange, disabled, label, description, className, children }: RadioGroupProps) {
  const id = useId();
  const ctx: RadioGroupCtx = { name: name ?? `radio-${id}`, value, onChange, disabled };
  return (
    <RadioGroupContext.Provider value={ctx}>
      <fieldset className={cn('flex flex-col gap-3', className)} disabled={disabled}>
        {label && <legend className="text-base font-medium text-text-primary mb-1">{label}</legend>}
        {description && <p className="text-sm text-text-secondary -mt-1 mb-2">{description}</p>}
        {children}
      </fieldset>
    </RadioGroupContext.Provider>
  );
}

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'value' | 'onChange'> {
  value: string;
  label?: ReactNode;
  description?: string;
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { value, label, description, className, id: idProp, disabled, ...rest },
  ref,
) {
  const ctx = useContext(RadioGroupContext);
  const generated = useId();
  const id = idProp ?? generated;
  const checked = ctx ? ctx.value === value : rest.checked;
  const isDisabled = disabled ?? ctx?.disabled;
  return (
    <label
      htmlFor={id}
      className={cn(
        'group inline-flex items-start gap-3 cursor-pointer min-h-[44px] py-1',
        isDisabled && 'cursor-not-allowed opacity-60',
        className,
      )}
    >
      <span className="relative inline-flex pt-0.5">
        <input
          ref={ref}
          id={id}
          type="radio"
          name={ctx?.name}
          value={value}
          checked={checked}
          disabled={isDisabled}
          onChange={(e) => ctx?.onChange?.(e.currentTarget.value)}
          className="peer sr-only"
          {...rest}
        />
        <span
          aria-hidden
          className={cn(
            'inline-flex size-5 items-center justify-center rounded-full border border-border-strong bg-bg-surface-2 transition-colors',
            'peer-checked:border-accent-base',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent-base',
          )}
        >
          <span className="size-2.5 rounded-full bg-accent-base opacity-0 peer-checked:opacity-100 group-has-[input:checked]:opacity-100" />
        </span>
      </span>
      {(label || description) && (
        <span className="flex flex-col">
          {label && <span className="text-base text-text-primary leading-tight">{label}</span>}
          {description && <span className="text-sm text-text-secondary mt-0.5">{description}</span>}
        </span>
      )}
    </label>
  );
});
